import { useEffect } from 'react';
import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes';
import type { ThemeProviderProps } from 'next-themes';

// Cookie name for cross-subdomain theme sync
const THEME_COOKIE_NAME = 'commit-you-theme';

// Get cookie domain based on hostname
function getCookieDomain(): string {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname.includes('127.0.0.1')) {
    return 'localhost';
  }
  return '.commit.you';
}

// Read theme from cookie
function getThemeFromCookie(): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${THEME_COOKIE_NAME}=([^;]+)`));
  return match ? match[2] : null;
}

// Write theme to cookie (shared across subdomains)
function setThemeCookie(theme: string) {
  const domain = getCookieDomain();
  const isLocalhost = domain === 'localhost';
  const secure = isLocalhost ? '' : '; Secure';
  document.cookie = `${THEME_COOKIE_NAME}=${theme}; path=/; domain=${domain}; max-age=31536000; SameSite=Lax${secure}`;
}

// Component that syncs theme to cookie
function ThemeCookieSync() {
  const { theme, setTheme } = useTheme();

  // On mount, check if there's a theme in cookie that differs from localStorage
  useEffect(() => {
    const cookieTheme = getThemeFromCookie();
    if (cookieTheme && cookieTheme !== theme) {
      setTheme(cookieTheme);
    }
  }, []);

  // When theme changes, sync to cookie
  useEffect(() => {
    if (theme) {
      setThemeCookie(theme);
    }
  }, [theme]);

  return null;
}

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      {...props}
    >
      <ThemeCookieSync />
      {children}
    </NextThemesProvider>
  );
}
